import { useState, useEffect, useCallback } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import { Link, useNavigate } from "react-router-dom";
import { Panel, PanelBody, PanelHeader } from "../../components/panel/panel";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector, useDispatch } from "react-redux";
import { updateUserData } from "../../app/auth/authSlice";
import moment from "moment";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import CombinedDateTimePicker from "../../components/CustomComponents/DateTimePicker/CombinedDateTimePicker";

function AddVoucher() {
  const dispatch = useDispatch();
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const hasProfileData = !!profileData;
  const profileCompCurrency = profileData?.compCurrency;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  
  const [compCurrency, setCompCurrency] = useState("GBP"); // Added state for CompCurrency
  const [voucherData, setVoucherData] = useState({
    Voucher_Name: "",
    Voucher_Code: "",
    Voucher_Detail: "",
    Voucher_DiscountPercentage: "",
    Voucher_MinimumOrderTotalAmount: "",
    Voucher_MaxUsesLimit: "",
    Voucher_NoOfUses: "",
    Voucher_Active: false,
    MultiAllow_Active: false,
    Voucher_IsMobileActive: false,
    Voucher_StartDate: null,
    Voucher_EndDate: null,
  });

  // Currency symbol mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
    // Add more currency symbols as needed
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  // Fetch CompCurrency
  const fetchCompCurrency = useCallback(async () => {
    try {
      // First, try to get from Redux state
      if (hasProfileData) {
        setCompCurrency(profileCompCurrency || "GBP");
        return;
      }

      // If not in Redux, try to fetch from API
      try {
        const response = await axiosInstance.get(APIs.GET_COMPANY_PROFILE);
        if (response.data?.data) {
          const currency = response.data.data.compCurrency || "GBP";
          setCompCurrency(currency);
          // Update Redux with fetched data
          dispatch(updateUserData({ profileData: response.data.data }));
          return;
        }
      } catch (apiErr) {
        console.error("Failed to fetch profile from API:", apiErr);
      }

      // If both fail, use default but don't show error toast on initial load
      setCompCurrency("GBP");
    } catch (err) {
      console.error("Error in fetchCompCurrency:", err);
      setCompCurrency("GBP"); // Use default instead of showing error
    }
  }, [hasProfileData, profileCompCurrency, dispatch]);

  useEffect(() => {
    fetchCompCurrency();
  }, [hasProfileData, profileCompCurrency, fetchCompCurrency]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setVoucherData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleToggleChange = (checked, name) => {
    setVoucherData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleDateRangeChange = (field, value) => {
    setVoucherData((prev) => {
      const momentValue =
        value && moment(value).isValid() ? moment(value) : null;
      const newVoucherData = { ...prev, [field]: momentValue };

      if (
        momentValue &&
        field === "Voucher_StartDate" &&
        newVoucherData.Voucher_EndDate
      ) {
        const endDateTime = moment(newVoucherData.Voucher_EndDate);
        if (endDateTime.isBefore(momentValue)) {
          newVoucherData.Voucher_EndDate = momentValue.clone();
        }
      }

      return newVoucherData;
    });
  };

  // Note: Date validation is now handled by CombinedDateTimePicker's built-in validation

  const handleSubmit = async () => {
    // Voucher Name validation (MANDATORY)
    if (!voucherData.Voucher_Name.trim()) {
      showErrorToast("Voucher name is required");
      return;
    }

    // Voucher Code validation (MANDATORY)
    if (!voucherData.Voucher_Code.trim()) {
      showErrorToast("Voucher code is required");
      return;
    }

    // Maximum Uses Limit validation (MANDATORY)
    if (!voucherData.Voucher_MaxUsesLimit || voucherData.Voucher_MaxUsesLimit === "") {
      showErrorToast("Maximum uses limit is required");
      return;
    }

    if (isNaN(voucherData.Voucher_MaxUsesLimit) || parseInt(voucherData.Voucher_MaxUsesLimit) <= 0) {
      showErrorToast("Maximum uses limit must be a positive number");
      return;
    }

    // Discount Percentage validation (MANDATORY)
    if (!voucherData.Voucher_DiscountPercentage || voucherData.Voucher_DiscountPercentage === "") {
      showErrorToast("Discount percentage is required");
      return;
    }

    if (isNaN(voucherData.Voucher_DiscountPercentage) || parseFloat(voucherData.Voucher_DiscountPercentage) <= 0) {
      showErrorToast("Discount percentage must be a valid positive number");
      return;
    }

    if (parseFloat(voucherData.Voucher_DiscountPercentage) > 100) {
      showErrorToast("Discount percentage cannot exceed 100%");
      return;
    }

    // Optional field validations (only validate if provided)
    if (voucherData.Voucher_MinimumOrderTotalAmount && voucherData.Voucher_MinimumOrderTotalAmount !== "") {
      if (isNaN(voucherData.Voucher_MinimumOrderTotalAmount) || parseFloat(voucherData.Voucher_MinimumOrderTotalAmount) < 0) {
        showErrorToast("Minimum order total must be a valid positive number");
        return;
      }
    }

    if (voucherData.Voucher_Detail && voucherData.Voucher_Detail.length > 250) {
      showErrorToast("Voucher detail must not exceed 250 characters");
      return;
    }

    if (voucherData.Voucher_StartDate && voucherData.Voucher_EndDate) {
      if (moment(voucherData.Voucher_EndDate).isBefore(voucherData.Voucher_StartDate)) {
        showErrorToast("End date cannot be before start date");
        return;
      }
    }

    // Number of Uses validation (if provided)
    if (
      voucherData.Voucher_NoOfUses &&
      (isNaN(voucherData.Voucher_NoOfUses) || voucherData.Voucher_NoOfUses < 0)
    ) {
      showErrorToast("Number of uses must be a positive number");
      return;
    }

    if (!CompID) {
      showErrorToast("Company ID is missing. Please sign in again.");
      return;
    }

    setLoading(true);

    try {
      const voucherPayload = {
        Voucher_ID: 0,
        CompID: CompID,
        Voucher_Name: voucherData.Voucher_Name,
        Voucher_Code: voucherData.Voucher_Code,
        Voucher_Detail: voucherData.Voucher_Detail || "",
        Voucher_DiscountPercentage:
          parseFloat(voucherData.Voucher_DiscountPercentage) || 0,
        Voucher_MinimumOrderTotalAmount:
          parseFloat(voucherData.Voucher_MinimumOrderTotalAmount) || 0,
        Voucher_StartDate: (() => {
          if (!voucherData.Voucher_StartDate) return null;
          const parsed = moment(voucherData.Voucher_StartDate);
          return parsed.isValid() ? parsed.format() : null;
        })(),
        Voucher_EndDate: (() => {
          if (!voucherData.Voucher_EndDate) return null;
          const parsed = moment(voucherData.Voucher_EndDate);
          return parsed.isValid() ? parsed.format() : null;
        })(),
        Voucher_MaxUsesLimit: parseInt(voucherData.Voucher_MaxUsesLimit) || 0,
        Voucher_NoOfUses: parseInt(voucherData.Voucher_NoOfUses) || 0,
        Voucher_Active: voucherData.Voucher_Active,
        UpdateDateTime: new Date().toISOString(),
        MultiAllow_Active: voucherData.MultiAllow_Active,
        Voucher_IsMobileActive: voucherData.Voucher_IsMobileActive,
      };

      const response = await axiosInstance.post(
        APIs.POST_ADD_VOUCHER,
        voucherPayload
      );

      if (!response.data.status) {
        showErrorToast(
          response.data.message ||
            response.data.Message ||
            "Failed to add voucher"
        );
        return;
      }

      showSuccessToast("Voucher added successfully");
      navigate("/voucher/manageVoucher");
    } catch (error) {
      const errorMessage =
        error.response?.data?.Message ||
        error.response?.data?.message ||
        (Array.isArray(error.response?.data?.errors)
          ? error.response.data.errors.join(", ")
          : null) ||
        (error.response?.data?.errors &&
        typeof error.response.data.errors === "object"
          ? Object.values(error.response.data.errors).flat().join(", ")
          : null) ||
        "An error occurred while adding the voucher";
      showErrorToast(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/voucher/manageVoucher">Manage Vouchers</Link>
            </li>
            <li className="breadcrumb-item active">Create Voucher</li>
          </ol>
          <h1 className="page-header mb-0">Create Voucher</h1>
        </div>
        <div className="ms-auto">
          <button
            className="btn px-4"
            onClick={handleSubmit}
            disabled={loading}
            style={{ 
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: 'none',
              color: 'white',
              borderRadius: '20px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: `0 2px 8px ${themeColor}40`,
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }
            }}
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>

      <div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>General</PanelHeader>

          <PanelBody>
            <div className="row mb-4">
              <div className="col-6">
                <FormInput
                  label="Voucher Name *"
                  placeHolder="Enter voucher name"
                  type="text"
                  name="Voucher_Name"
                  value={voucherData.Voucher_Name}
                  onchange={handleInputChange}
                  required
                />
              </div>
              <div className="col-6">
                <FormInput
                  label="Voucher Code *"
                  placeHolder="Enter voucher code"
                  type="text"
                  name="Voucher_Code"
                  value={voucherData.Voucher_Code}
                  onchange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="row mb-4">
              {/* <div className="col-6">
                <FormInput
                  label="Number of Uses"
                  placeHolder="e.g. 0"
                  type="number"
                  name="Voucher_NoOfUses"
                  value={voucherData.Voucher_NoOfUses}
                  onchange={handleInputChange}
                />
              </div> */}

             
            </div>

            <div className="row mb-4">
              <div className="col-6 mb-2">
                <FormInput
                  label={`Minimum Order Total (${getCurrencySymbol(compCurrency)})`}
                  placeHolder={`${getCurrencySymbol(compCurrency)}0.00`}
                  type="number"
                  name="Voucher_MinimumOrderTotalAmount"
                  value={voucherData.Voucher_MinimumOrderTotalAmount}
                  onchange={handleInputChange}
                  prepend={getCurrencySymbol(compCurrency)}
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="col-6">
                <FormInput
                  label="Maximum Uses Limit *"
                  placeHolder="e.g. 100"
                  type="number"
                  name="Voucher_MaxUsesLimit"
                  value={voucherData.Voucher_MaxUsesLimit}
                  onchange={handleInputChange}
                  required
                  min="1"
                  step="1"
                />
              </div>
            </div>

            <div className="row mb-4">
               <div className="col-6  ">
                <FormInput
                  label="Discount Percentage *"
                  placeHolder="e.g. 10%"
                  type="number"
                  name="Voucher_DiscountPercentage"
                  value={voucherData.Voucher_DiscountPercentage}
                  onchange={handleInputChange}
                  required
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>
              <div className="col-6">
                <label 
                  className="form-label fw-medium"
                  style={{ color: isDarkMode ? '#ffffff' : '#000000' }}
                >
                  <h6>Voucher Detail</h6>
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Enter voucher description"
                  name="Voucher_Detail"
                  value={voucherData.Voucher_Detail}
                  onChange={handleInputChange}
                  maxLength={250}
                  style={{
                    borderRadius: "10px",
                    border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                    background: isDarkMode ? "#2d3436" : "#F9F9F9",
                    color: isDarkMode ? "#ffffff" : "#000000",
                    transition: "all 0.3s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = themeColor;
                    e.target.style.boxShadow = `0 0 0 0.2rem ${themeColor}40`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = isDarkMode ? "#404040" : "#D9D9D9";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <small className="text-muted" style={{ fontSize: '11px' }}>
                  {voucherData.Voucher_Detail.length}/250 characters (optional)
                </small>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-6">
                <CombinedDateTimePicker
                  label="Start Date"
                  value={voucherData.Voucher_StartDate}
                  onChange={(date) =>
                    handleDateRangeChange("Voucher_StartDate", date)
                  }
                  ampm={false}
                  placeholder="Select start date and time"
                  name="Voucher_StartDate"
                />
              </div>
              <div className="col-6">
                <CombinedDateTimePicker
                  label="End Date"
                  value={voucherData.Voucher_EndDate}
                  onChange={(date) =>
                    handleDateRangeChange("Voucher_EndDate", date)
                  }
                  ampm={false}
                  placeholder="Select end date and time"
                  name="Voucher_EndDate"
                  minDate={voucherData.Voucher_StartDate}
                />
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-4">
                <ToggleSwitch
                  isChecked={voucherData.Voucher_IsMobileActive}
                  onToggle={(checked) => handleToggleChange(checked, "Voucher_IsMobileActive")}
                  label="Online Active"
                  name="Voucher_IsMobileActive"
                  themeColor={themeColor}
                />
                <p className="text-muted mt-2">
                  Allow voucher use on online platforms
                </p>
              </div>
              <div className="col-4">
                <ToggleSwitch
                  isChecked={voucherData.Voucher_Active}
                  onToggle={(checked) => handleToggleChange(checked, "Voucher_Active")}
                  label="Active"
                  name="Voucher_Active"
                  themeColor={themeColor}
                />
                <p className="text-muted mt-2">Enable or disable the voucher</p>
              </div>
            </div>
          </PanelBody>
        </Panel>
      </div>
    </div>
  );
}

export default AddVoucher;